import { useState, useEffect } from 'react';
import { FileText, Download, BarChart2, Clock, Wrench, AlertTriangle, FileDown, QrCode } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

export default function Informes() {
  const [kpis, setKpis] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingQr, setExportingQr] = useState(false);
  const [exportingManual, setExportingManual] = useState(false);
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState('');

  useEffect(() => {
    Promise.all([api.get('/reports/kpi'), api.get('/equipment')]).then(([kpiRes, eqRes]) => {
      setKpis(kpiRes.data);
      setEquipment(eqRes.data);
    });
  }, []);

  const downloadBlob = async (url, filename) => {
    const token = localStorage.getItem('gmao_token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(a.href);
  };

  const handleExportXML = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ year: exportYear });
      if (selectedEquipment) params.append('equipment_id', selectedEquipment);
      await downloadBlob(`${API_URL}/api/reports/export/xml?${params}`, `GMAO_GESPESA_${exportYear}.xml`);
    } finally { setExporting(false); }
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      await downloadBlob(`${API_URL}/api/reports/export/pdf?year=${exportYear}`, `GMAO_Informe_${exportYear}.pdf`);
    } finally { setExportingPdf(false); }
  };

  const handleExportQR = async () => {
    setExportingQr(true);
    try {
      await downloadBlob(`${API_URL}/api/equipment/qr-sheet/pdf?base_url=${encodeURIComponent(APP_URL)}`, 'GMAO_QR_Equipos.pdf');
    } finally { setExportingQr(false); }
  };

  const handleExportManual = async () => {
    setExportingManual(true);
    try {
      await downloadBlob(`${API_URL}/api/user-manual/pdf`, 'GMAO_Manual_Usuario.pdf');
    } finally { setExportingManual(false); }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Informes y Exportación</h1>
        <p className="text-sm text-gray-500 mt-0.5">Análisis de datos y exportación para GESPESA</p>
      </div>

      {/* Export XML Card */}
      <div className="bg-white rounded-xl border-2 border-[#16a34a]/20 shadow-sm p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#16a34a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText size={24} className="text-[#16a34a]" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Exportación XML para GESPESA
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Genera el fichero .xml con todos los datos del GMAO para integración con el sistema GESPESA. 
              Incluye horómetro, órdenes de trabajo y alertas.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Año</label>
                <select
                  value={exportYear}
                  onChange={e => setExportYear(Number(e.target.value))}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
                  data-testid="export-year-select"
                >
                  {[2026, 2025, 2024, 2023].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Equipo (opcional)</label>
                <select
                  value={selectedEquipment}
                  onChange={e => setSelectedEquipment(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#16a34a] focus:outline-none w-56"
                  data-testid="export-equipment-select"
                >
                  <option value="">Todos los equipos</option>
                  {equipment.map(eq => (
                    <option key={eq.id} value={eq.id}>{eq.name}</option>
                  ))}
                </select>
              </div>
              <div className="mt-5 flex gap-2 flex-wrap">
                <button
                  onClick={handleExportXML}
                  disabled={exporting}
                  className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  data-testid="export-xml-btn"
                >
                  <Download size={15} />
                  {exporting ? 'Generando...' : 'Exportar XML (GESPESA)'}
                </button>
                <button
                  onClick={handleExportPDF}
                  disabled={exportingPdf}
                  className="flex items-center gap-2 bg-[#1d4ed8] hover:bg-[#1e40af] text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  data-testid="export-pdf-btn"
                >
                  <FileDown size={15} />
                  {exportingPdf ? 'Generando PDF...' : 'Exportar Informe PDF'}
                </button>
                <button
                  onClick={handleExportQR}
                  disabled={exportingQr}
                  className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  data-testid="export-qr-btn"
                >
                  <QrCode size={15} />
                  {exportingQr ? 'Generando QR...' : 'Imprimir QR Equipos'}
                </button>
                <button
                  onClick={handleExportManual}
                  disabled={exportingManual}
                  className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
                  data-testid="export-manual-btn"
                >
                  <FileDown size={15} />
                  {exportingManual ? 'Generando Manual...' : 'Manual de Usuario PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Summary */}
      {kpis && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Equipos Totales', value: kpis.total_equipment, icon: BarChart2, color: '#16a34a' },
              { label: 'Horas Este Año', value: kpis.hours_this_year?.toLocaleString('es-ES') + ' h', icon: Clock, color: '#3b82f6' },
              { label: 'OTs Completadas', value: kpis.completed_wo, icon: Wrench, color: '#8b5cf6' },
              { label: 'Alertas Activas', value: kpis.alert_count, icon: AlertTriangle, color: '#eab308' },
            ].map(stat => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: stat.color + '15' }}>
                      <Icon size={18} style={{ color: stat.color }} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{stat.label}</p>
                      <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Barlow, sans-serif', color: stat.color }}>{stat.value}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Monthly Hours Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Horas de Operación Mensuales
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={kpis.monthly_trend || []} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`${v} h`, 'Horas']} />
                <Bar dataKey="hours" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top machines */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Equipos por Horas Totales de Operación
            </h3>
            <div className="space-y-3">
              {(kpis.top_machines || []).map((m, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right font-mono">{i + 1}.</span>
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-700 font-medium">{m.name}</span>
                      <span className="font-mono text-gray-500">{(m.hours || 0).toLocaleString('es-ES')} h</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${((m.hours || 0) / (kpis.top_machines?.[0]?.hours || 1)) * 100}%`,
                          backgroundColor: i === 0 ? '#16a34a' : i < 3 ? '#3b82f6' : '#94a3b8',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
